/**
 * The examination in words, drafted from the record.
 *
 * The legacy app printed a "clinical summary" and a "fitness statement" under the
 * physician's form. Ours is drafted the same way — from the answers and
 * readings actually recorded, never from anything assumed — and is offered
 * to the physician as a draft they can take into their advice, and printed
 * on the worker's report as a plain-language account of what was found.
 *
 * Everything here is a reading of the record. It names what was answered
 * "yes", lists what was not within range, and says nothing where nothing
 * was recorded. It does not decide fitness: the fitness statement is only
 * written once a physician has signed a decision, and repeats that decision.
 *
 * Dependency-free, like derived-vitals.ts beside it: the SaaS shows the
 * same draft the API prints.
 */

import { VITAL_CODE, bmiBand, bloodPressureBand, bmi as bmiOf } from './derived-vitals';
import { TOOTH_CHART_CODE, formatToothChart, toothFindings } from './dental';

/** One recorded answer or reading, by catalogue code. */
export interface SummaryResult {
  code: string;
  name: string;
  /** The stored value: a number, the option display text, free text, or a yes/no. */
  value: number | string | boolean | null;
  /** The option code of a fixed-answer test, when there is one. */
  optionCode?: string | null;
  unit?: string | null;
  flag?: string | null;
  category?: string | null;
}

export interface SummarySubject {
  fullName: string;
  sex?: string | null;
  ageYears?: number | null;
  jobRole?: string | null;
  examinationType?: string | null;
  examinedOn?: string | Date | null;
}

export interface SummaryDecision {
  decision: string;
  restrictions?: Array<{ label: string }>;
  physicianName?: string | null;
  signedAt?: string | Date | null;
  validUntil?: string | Date | null;
}

export interface ClinicalSummary {
  /** Paragraphs, in reading order; empty when nothing was recorded. */
  paragraphs: string[];
  /** The decision in one sentence, only once one is signed. */
  fitnessStatement: string | null;
  /** The whole thing as text, paragraphs separated by blank lines. */
  text: string;
}

// ── The questionnaire, by code ──────────────────────────────────
//
// Codes are grouped here rather than by prefix alone because the catalogue
// grew in two hands: the co-editor's HIST-* fields and the ones added for
// Legacy-app parity. Anything not listed is still read, under "other findings",
// when it is flagged.

const HISTORY: Array<[string, string]> = [
  ['HIST-DIABETES', 'diabetes'], ['HIST-HYPERTENSION', 'hypertension'], ['HIST-ASTHMA', 'asthma or COPD'],
  ['HIST-TB', 'tuberculosis'], ['HIST-HEART', 'heart disease'], ['HIST-EPILEPSY', 'epilepsy or seizures'],
  ['HIST-KIDNEY-LIVER', 'kidney or liver disease'], ['HIST-CANCER', 'cancer'], ['HIST-PSYCHIATRIC', 'psychiatric illness'],
  ['HIST-POLIO', 'polio'], ['HIST-SURGERY', 'previous surgery'], ['HIST-HOSPITALISATION', 'recent hospitalisation'],
  ['HIST-ALLERGY', 'allergies'],
];
const FAMILY: Array<[string, string]> = [
  ['FAM-DIABETES', 'diabetes'], ['FAM-HYPERTENSION', 'hypertension'], ['FAM-HEART', 'heart disease'],
  ['FAM-CANCER', 'cancer'], ['FAM-ASTHMA', 'asthma'], ['FAM-PSYCHIATRIC', 'psychiatric illness'],
];
const EXPOSURE: Array<[string, string]> = [
  ['OCC-EXPOSURE-DUST', 'dust, fumes or chemicals'], ['OCC-EXPOSURE-NOISE', 'noise'], ['OCC-EXPOSURE-HEAT', 'heat'],
  ['OCC-EXPOSURE-VIBRATION', 'vibration'], ['OCC-REPETITIVE', 'repetitive motion'],
];
const HABITS: Array<[string, string]> = [
  ['HIST-SMOKING', 'smokes'], ['HIST-TOBACCO', 'uses tobacco'], ['HIST-GUTKHA', 'uses gutkha'], ['HIST-ALCOHOL', 'takes alcohol'],
];
const WELLBEING: Array<[string, string]> = [
  ['WELL-STRESS', 'feels stressed'], ['WELL-LOW-MOOD', 'feels anxious or low'],
];
const SYSTEMS: Array<[string, string]> = [
  ['PHY-EAR', 'ears'], ['PHY-NOSE', 'nose'], ['PHY-THROAT', 'throat'], ['PHY-TEETH', 'teeth'], ['PHY-NAILS', 'nails'],
  ['PHY-SKIN', 'skin'], ['PHY-LYMPH', 'lymph nodes'], ['PHY-HERNIA', 'hernia'], ['PHY-RESP', 'respiratory system'],
  ['PHY-CVS', 'cardiovascular system'], ['PHY-CNS', 'central nervous system'], ['PHY-GI', 'gastrointestinal system'],
  ['PHY-MSK', 'musculoskeletal system'], ['PHY-SPINE', 'spine'],
];

const DECISION_WORDS: Record<string, string> = {
  FIT: 'FIT for the assigned duties',
  FIT_WITH_RESTRICTION: 'FIT for duty with the restrictions listed',
  TEMPORARILY_UNFIT: 'TEMPORARILY UNFIT for duty, pending re-examination',
  UNFIT: 'UNFIT for the assigned duties',
  FURTHER_EVALUATION: 'in need of FURTHER EVALUATION before a decision on fitness',
};

const yes = (r: SummaryResult | undefined): boolean =>
  !!r && (r.optionCode === 'YES' || r.value === true || (typeof r.value === 'string' && /^yes$/i.test(r.value.trim())));
const answered = (r: SummaryResult | undefined): boolean =>
  !!r && r.value != null && r.value !== '' && r.optionCode !== 'NOT_ASSESSED';
const text = (r: SummaryResult | undefined): string | null => {
  if (!r || r.value == null) return null;
  const s = String(r.value).trim();
  return s === '' ? null : s;
};
const num = (r: SummaryResult | undefined): number | null =>
  r && typeof r.value === 'number' && Number.isFinite(r.value) ? r.value : null;

function list(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
function honorific(sex?: string | null): string {
  return sex === 'MALE' ? 'Mr' : sex === 'FEMALE' ? 'Ms' : '';
}
function dateWords(d: string | Date | null | undefined): string | null {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function clinicalSummary(
  results: SummaryResult[], subject: SummarySubject, decision?: SummaryDecision | null,
): ClinicalSummary {
  const by = new Map(results.map((r) => [r.code, r]));
  const get = (code: string) => by.get(code);
  const paragraphs: string[] = [];
  const name = [honorific(subject.sex), subject.fullName].filter(Boolean).join(' ');

  // ── Who, and what they came for ───────────────────────────────
  const who: string[] = [];
  if (subject.ageYears != null) who.push(`aged ${subject.ageYears}`);
  if (subject.jobRole) who.push(`working as ${subject.jobRole}`);
  const complaints = text(get('HIST-COMPLAINTS'));
  paragraphs.push(
    `${name}${who.length ? `, ${who.join(', ')},` : ''} was examined${subject.examinedOn ? ` on ${dateWords(subject.examinedOn)}` : ''}.`
    + (complaints ? ` Presenting complaints: ${complaints}.` : (answered(get('HIST-COMPLAINTS')) ? '' : ' No presenting complaints were recorded.')),
  );

  // ── History ───────────────────────────────────────────────────
  const history = HISTORY.filter(([c]) => yes(get(c))).map(([, w]) => w);
  const historyAnswered = HISTORY.some(([c]) => answered(get(c)));
  const details: string[] = [];
  for (const [code, label] of [['HIST-SURGERY-DETAILS', 'surgery'], ['HIST-HOSPITALISATION-DETAILS', 'hospitalisation'], ['HIST-ALLERGY-DETAILS', 'allergies']] as const) {
    const t = text(get(code)); if (t) details.push(`${label}: ${t}`);
  }
  const meds = text(get('HIST-MEDICATION'));
  const notifiable = text(get('HIST-NOTIFIABLE'));
  const treated = yes(get('HIST-TREATMENT'));
  if (historyAnswered || meds || notifiable || details.length) {
    const parts: string[] = [];
    parts.push(history.length ? `History of ${list(history)}.` : 'No significant past medical history.');
    if (details.length) parts.push(`${details.join('; ')}.`);
    if (notifiable) parts.push(`Notifiable disease recorded: ${notifiable}.`);
    if (treated) parts.push('Currently under treatment.');
    if (meds) parts.push(`Current medication: ${meds}.`);
    paragraphs.push(parts.join(' '));
  }

  // ── Family ────────────────────────────────────────────────────
  const family = FAMILY.filter(([c]) => yes(get(c))).map(([, w]) => w);
  const familyText = text(get('HIST-FAMILY'));
  if (FAMILY.some(([c]) => answered(get(c))) || familyText) {
    paragraphs.push(
      (family.length ? `Family history of ${list(family)}.` : 'No significant family history.')
      + (familyText ? ` ${familyText}.` : ''),
    );
  }

  // ── Occupation and lifestyle ──────────────────────────────────
  const occ: string[] = [];
  const years = num(get('OCC-YEARS-IN-JOB'));
  if (years != null) occ.push(`${years} year${years === 1 ? '' : 's'} in the current job`);
  const exposures = EXPOSURE.filter(([c]) => yes(get(c))).map(([, w]) => w);
  if (exposures.length) occ.push(`exposed to ${list(exposures)}`);
  else if (EXPOSURE.some(([c]) => answered(get(c)))) occ.push('no occupational exposure reported');
  if (answered(get('OCC-PPE'))) {
    const ppe = text(get('OCC-PPE-DETAILS'));
    occ.push(yes(get('OCC-PPE')) ? `uses protective equipment${ppe ? ` (${ppe})` : ''}` : 'does not use protective equipment');
  }
  if (yes(get('OCC-INJURY'))) {
    const inj = text(get('OCC-INJURY-DETAILS'));
    occ.push(`recent workplace injury${inj ? `: ${inj}` : ''}`);
  }
  const habits = HABITS.filter(([c]) => yes(get(c))).map(([, w]) => w);
  const smokingDetails = text(get('LIFE-SMOKING-DETAILS'));
  const alcoholFreq = text(get('LIFE-ALCOHOL-FREQUENCY'));
  const life: string[] = [];
  if (habits.length) {
    life.push(list(habits) + (smokingDetails ? ` (${smokingDetails})` : '') + (alcoholFreq && habits.includes('takes alcohol') ? `, alcohol ${alcoholFreq.toLowerCase()}` : ''));
  } else if (HABITS.some(([c]) => answered(get(c)))) life.push('no tobacco, smoking or alcohol');
  const activity = text(get('LIFE-ACTIVITY')); if (activity) life.push(`physical activity ${activity.toLowerCase()}`);
  const diet = text(get('LIFE-DIET')); if (diet) life.push(`${diet.toLowerCase()} diet`);
  const sleep = num(get('LIFE-SLEEP-HOURS')); if (sleep != null) life.push(`sleeps ${sleep} h a night`);
  const water = num(get('LIFE-WATER-LITRES')); if (water != null) life.push(`drinks about ${water} L of water a day`);
  if (occ.length || life.length) {
    const s: string[] = [];
    if (occ.length) s.push(`Occupation: ${occ.join('; ')}.`);
    if (life.length) s.push(`Lifestyle: ${life.join('; ')}.`);
    paragraphs.push(s.join(' '));
  }

  // ── Immunisation and wellbeing ────────────────────────────────
  const imm: string[] = [];
  if (answered(get('IMM-TETANUS'))) imm.push(yes(get('IMM-TETANUS')) ? 'tetanus booster within the last six months' : 'no tetanus booster in the last six months');
  if (answered(get('IMM-RECENT-CHECKUP'))) {
    const d = text(get('IMM-RECENT-CHECKUP-DETAILS'));
    imm.push(yes(get('IMM-RECENT-CHECKUP')) ? `a recent health check-up${d ? ` (${d})` : ''}` : 'no recent health check-up');
  }
  const well = WELLBEING.filter(([c]) => yes(get(c))).map(([, w]) => w);
  if (answered(get('WELL-SUPPORT')) && !yes(get('WELL-SUPPORT'))) well.push('reports no support network');
  if (imm.length || well.length) {
    const s: string[] = [];
    if (imm.length) s.push(`Immunisation: ${list(imm)}.`);
    if (well.length) s.push(`Wellbeing: ${list(well)}.`);
    else if (WELLBEING.some(([c]) => answered(get(c)))) s.push('Wellbeing: no stress or low mood reported.');
    paragraphs.push(s.join(' '));
  }

  // ── Examination ───────────────────────────────────────────────
  const exam: string[] = [];
  const sys = num(get(VITAL_CODE.SYSTOLIC)), dia = num(get(VITAL_CODE.DIASTOLIC));
  if (sys != null && dia != null) {
    const band = bloodPressureBand(sys, dia);
    exam.push(`blood pressure ${sys}/${dia} mmHg${band ? ` (${band.label.toLowerCase()})` : ''}`);
  }
  const pulse = num(get(VITAL_CODE.PULSE)); if (pulse != null) exam.push(`pulse ${pulse}/min`);
  const temp = num(get(VITAL_CODE.TEMPERATURE)); if (temp != null) exam.push(`temperature ${temp} °C`);
  const spo2 = num(get(VITAL_CODE.SPO2)); if (spo2 != null) exam.push(`SpO₂ ${spo2}%`);
  const h = num(get(VITAL_CODE.HEIGHT)), w = num(get(VITAL_CODE.WEIGHT));
  const bmiValue = num(get(VITAL_CODE.BMI)) ?? bmiOf(h, w);
  if (h != null && w != null) exam.push(`height ${h} cm, weight ${w} kg`);
  if (bmiValue != null) {
    const band = bmiBand(bmiValue);
    exam.push(`BMI ${bmiValue} kg/m²${band ? ` (${band.code.toLowerCase()}, WHO)` : ''}`);
  }
  const waist = num(get(VITAL_CODE.WAIST)); if (waist != null) exam.push(`waist ${waist} cm`);
  const abnormalSystems = SYSTEMS.filter(([c]) => {
    const r = get(c); if (!r) return false;
    if (r.flag && r.flag !== 'NORMAL') return true;
    const t = text(r); return !!t && !/^(normal|nad|nil|no abnormality( detected)?|unremarkable|within normal limits|wnl)\.?$/i.test(t);
  }).map(([c, w]) => `${w}: ${text(get(c))}`);
  const systemsAnswered = SYSTEMS.filter(([c]) => answered(get(c))).length;
  const general = text(get('PHY-EXAM'));
  if (exam.length || systemsAnswered || general) {
    const s: string[] = [];
    if (exam.length) s.push(`On examination: ${exam.join(', ')}.`);
    if (general) s.push(`General examination: ${general}.`);
    if (abnormalSystems.length) s.push(`Findings — ${abnormalSystems.join('; ')}.`);
    else if (systemsAnswered) s.push(`Systems examined (${systemsAnswered}) were recorded as normal.`);
    paragraphs.push(s.join(' '));
  }

  // ── Teeth and mouth ───────────────────────────────────────────
  const chart = get(TOOTH_CHART_CODE);
  const dentalDx = text(get('DEN-DIAGNOSIS'));
  const dentalYes = results.filter((r) => r.code.startsWith('DEN-') && r.code !== TOOTH_CHART_CODE && yes(r)).map((r) => r.name.replace(/\?$/, '').toLowerCase());
  if (chart || dentalDx || dentalYes.length) {
    const d: string[] = [];
    if (chart) d.push(toothFindings(text(chart)) ? `Teeth: ${formatToothChart(text(chart))}.` : 'Teeth: no findings on the chart.');
    if (dentalYes.length) d.push(`Mouth: ${list(dentalYes)}.`);
    if (dentalDx) d.push(`Dental diagnosis: ${dentalDx}.`);
    paragraphs.push(d.join(' '));
  }

  // ── Anything else out of range ────────────────────────────────
  const named = new Set([
    ...HISTORY, ...FAMILY, ...EXPOSURE, ...HABITS, ...WELLBEING, ...SYSTEMS,
  ].map(([c]) => c));
  // The questionnaire's own answers were narrated above; what is left is a
  // measurement or a laboratory value outside its band.
  const flagged = results.filter((r) => r.flag && r.flag !== 'NORMAL' && !named.has(r.code)
    && !/^(HIST|FAM|OCC|LIFE|IMM|WELL|PHY|DEN)-/.test(r.code)
    && ![VITAL_CODE.SYSTOLIC, VITAL_CODE.DIASTOLIC, VITAL_CODE.BMI].includes(r.code as never));
  // A LOW/HIGH/CRITICAL flag is a value outside its band; ABNORMAL is a
  // categorical answer recorded as abnormal and has no band to be outside of.
  // Saying "outside the reference range" of both misstated the second.
  const say = (r: (typeof flagged)[number]) =>
    `${r.name} ${typeof r.value === 'number' ? `${r.value}${r.unit ? ` ${r.unit}` : ''}` : String(r.value ?? '')}`.trim();
  const outOfBand = flagged.filter((r) => r.flag !== 'ABNORMAL');
  const abnormal = flagged.filter((r) => r.flag === 'ABNORMAL');
  if (outOfBand.length) paragraphs.push(`Outside the reference range: ${outOfBand.map(say).join('; ')}.`);
  if (abnormal.length) paragraphs.push(`Recorded as abnormal: ${abnormal.map(say).join('; ')}.`);

  // ── The physician's own words ─────────────────────────────────
  const impression = text(get('PHY-IMPRESSION'));
  const diagnosis = text(get('PHY-DIAGNOSIS'));
  const treatment = text(get('PHY-TREATMENT'));
  const remarks = text(get('PHY-REMARKS'));
  const notes: string[] = [];
  if (impression) notes.push(`General impression: ${impression}.`);
  if (diagnosis) notes.push(`Provisional diagnosis: ${diagnosis}.`);
  if (treatment) notes.push(`Treatment given or referred: ${treatment}.`);
  if (remarks) notes.push(`Remarks: ${remarks}.`);
  if (notes.length) paragraphs.push(notes.join(' '));

  // ── The decision, once signed ─────────────────────────────────
  let fitnessStatement: string | null = null;
  if (decision?.decision && decision.signedAt) {
    const words = DECISION_WORDS[decision.decision] ?? decision.decision.replace(/_/g, ' ').toLowerCase();
    const restr = decision.restrictions?.map((r) => r.label) ?? [];
    fitnessStatement = `This is to certify that ${name} was examined${decision.physicianName ? ` by ${decision.physicianName}` : ''}`
      + `${subject.examinedOn ? ` on ${dateWords(subject.examinedOn)}` : ''} and found ${words}`
      + (restr.length ? `: ${list(restr)}` : '') + '.'
      + (decision.validUntil ? ` The certificate is valid until ${dateWords(decision.validUntil)}.` : '');
  }

  // Nothing recorded at all: the first paragraph alone says only who came.
  const onlyIntro = paragraphs.length === 1 && !complaints && !answered(get('HIST-COMPLAINTS'));
  const out = onlyIntro && !fitnessStatement ? [] : paragraphs;
  return {
    paragraphs: out,
    fitnessStatement,
    text: [...out, ...(fitnessStatement ? [fitnessStatement] : [])].join('\n\n'),
  };
}
